import { EyeIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { leaveViewAs, viewAsState } from '../../services/view_as/boot';
import { endView, logViewPage } from '../../services/view_as/view_as_api';

const clock = (ms: number): string => {
    const s = Math.max(0, Math.floor(ms / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const cleanMessage = (m: unknown): string =>
    String(m ?? '').replace(/^\[[^\]]+\]:\s*/, '') || 'Not allowed while viewing as a customer';

// #region ViewAsBanner
/**
 * Shown in a "view as customer" tab, on every page: a colored frame around the screen and a
 * floating bar with whose dashboard this is, the mode, time left, and Exit. It also logs each
 * page opened, and explains any request the server refused.
 */
export const ViewAsBanner: React.FunctionComponent<{}> = (): React.ReactElement | null => {
    const state = React.useMemo(viewAsState, []);
    const location = useLocation();
    const [left, setLeft] = React.useState<number>(() => (state ? Date.parse(state.expires_at) - Date.now() : 0));
    const [flash, setFlash] = React.useState<string | null>(null);
    const [leaving, setLeaving] = React.useState(false);

    React.useEffect(() => {
        if (state) void logViewPage(state.token, location.pathname);
    }, [state, location.pathname]);

    React.useEffect(() => {
        if (!state) return undefined;
        const timer = window.setInterval(() => {
            const ms = Date.parse(state.expires_at) - Date.now();
            setLeft(ms);
            if (ms <= 0) leaveViewAs();
        }, 1000);
        return () => window.clearInterval(timer);
    }, [state]);

    React.useEffect(() => {
        let hide: number | undefined;
        const id = axios.interceptors.response.use(undefined, (err) => {
            const data = err?.response?.data;
            if (data?.view_as_ended) leaveViewAs();
            else if (data?.view_as_blocked) {
                setFlash(cleanMessage(data.message));
                window.clearTimeout(hide);
                hide = window.setTimeout(() => setFlash(null), 5000);
            }
            return Promise.reject(err);
        });
        return () => { axios.interceptors.response.eject(id); window.clearTimeout(hide); };
    }, []);

    if (!state) return null;
    const edit = state.view_as.mode === 'edit';

    const exit = async () => {
        setLeaving(true);
        await endView(state.token);
        window.close();
        leaveViewAs();
    };

    return (
        <>
            <div className={`pointer-events-none fixed inset-0 z-[70] border-[3px] ${edit ? 'border-rose-500' : 'border-amber-400'}`} aria-hidden="true" />
            <div className="fixed bottom-4 left-1/2 z-[71] flex max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-col items-center gap-2">
                {flash ? (
                    <div role="status" className="rounded-lg bg-zinc-900/95 px-3 py-1.5 text-xs font-medium text-amber-200 shadow-lg">
                        {flash}
                    </div>
                ) : null}
                <div className="flex items-center gap-3 rounded-full bg-zinc-900/95 py-1.5 pl-3 pr-1.5 text-xs text-white shadow-xl ring-1 ring-white/10 backdrop-blur">
                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${edit ? 'bg-rose-500' : 'bg-amber-400 text-zinc-900'}`}>
                        {edit ? <PencilSquareIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
                    </span>
                    <span className="min-w-0 truncate">
                        <span className="font-semibold">{state.view_as.brand_name}</span>
                        <span className="text-zinc-400"> · {edit ? 'changes are saved and recorded' : 'read-only'} · {state.view_as.rep_name}</span>
                    </span>
                    <span className="shrink-0 tabular-nums text-zinc-400">{clock(left)}</span>
                    <button
                        type="button"
                        disabled={leaving}
                        onClick={exit}
                        className="shrink-0 rounded-full bg-white px-3 py-1 font-semibold text-zinc-900 hover:bg-zinc-200 disabled:opacity-60"
                    >
                        {leaving ? 'Exiting…' : 'Exit'}
                    </button>
                </div>
            </div>
        </>
    );
};
